import React, { useState, useRef, useEffect, CSSProperties } from 'react';
import clsx from 'clsx';

export interface DropdownItem {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface DropdownProps {
  /**
   * The size of the dropdown
   */
  size?: 'sm' | 'base' | 'lg';
  
  /**
   * Whether the dropdown should take the full width of its container
   */
  fullWidth?: boolean;
  
  /**
   * Whether the dropdown is in an error state
   */
  error?: boolean;
  
  /**
   * Whether the dropdown is in a success state
   */
  success?: boolean;
  
  /**
   * Whether the dropdown is disabled
   */
  disabled?: boolean;
  
  /**
   * Icon to display at the start of the dropdown
   */
  startIcon?: React.ReactNode;
  
  /**
   * Label for the dropdown
   */
  label?: string;
  
  /**
   * Helper text to display below the dropdown
   */
  helperText?: string;
  
  /**
   * Placeholder text when no option is selected
   */
  placeholder?: string;
  
  /**
   * Array of items to display in the dropdown
   */
  items?: DropdownItem[];
  
  /**
   * Custom component to render when dropdown is open
   * If provided, items prop will be ignored
   */
  children?: React.ReactNode;
  
  /**
   * Currently selected value
   */
  value?: string | number;
  
  /**
   * Callback when selection changes
   */
  onChange?: (value: string | number) => void;
  
  /**
   * Callback when dropdown opens/closes
   */
  onToggle?: (isOpen: boolean) => void;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  style?: CSSProperties
}

/**
 * Caret icon component for dropdown
 */
const CaretIcon: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (
  <svg
    className={clsx('prometheus-dropdown__caret', {
      'prometheus-dropdown__caret--open': isOpen,
    })}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Dropdown component with consistent styling matching the Input design
 */
export const Dropdown: React.FC<DropdownProps> = ({
  size = 'base',
  fullWidth = false,
  error = false,
  success = false,
  disabled = false,
  startIcon,
  label,
  helperText,
  placeholder = 'Select an option...',
  items = [],
  children,
  value,
  onChange,
  onToggle,
  className,
  style,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownId = React.useId();
  
  // Find selected item for display
  const selectedItem = items.find(item => item.value === value);
  
  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;
      
      switch (event.key) {
        case 'Escape':
          setIsOpen(false);
          break;
        case 'ArrowDown':
          event.preventDefault();
          // Focus next item logic could be added here
          break;
        case 'ArrowUp':
          event.preventDefault();
          // Focus previous item logic could be added here
          break;
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);
  
  const handleToggle = () => {
    if (disabled) return;
    
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    onToggle?.(newIsOpen);
  };
  
  const handleItemSelect = (item: DropdownItem) => {
    if (item.disabled) return;
    
    onChange?.(item.value);
    setIsOpen(false);
    onToggle?.(false);
  };
  
  return (
    <div
      className={clsx(
        'prometheus-dropdown-wrapper',
        {
          'prometheus-dropdown-wrapper--full-width': fullWidth,
          'prometheus-dropdown-wrapper--disabled': disabled,
          'prometheus-dropdown-wrapper--error': error,
          'prometheus-dropdown-wrapper--success': success,
          'prometheus-dropdown-wrapper--open': isOpen,
        }
      )}
      style={style}
      ref={dropdownRef}
    >
      {label && (
        <label
          htmlFor={dropdownId}
          className="prometheus-dropdown__label"
        >
          {label}
        </label>
      )}
      
      <div className="prometheus-dropdown__container">
        {startIcon && (
          <span className="prometheus-dropdown__start-icon" aria-hidden="true">
            {startIcon}
          </span>
        )}
        
        <button
          id={dropdownId}
          type="button"
          className={clsx(
            'prometheus-dropdown',
            `prometheus-dropdown--${size}`,
            {
              'prometheus-dropdown--has-start-icon': startIcon,
              'prometheus-dropdown--error': error,
              'prometheus-dropdown--success': success,
              'prometheus-dropdown--disabled': disabled,
              'prometheus-dropdown--open': isOpen,
              'prometheus-dropdown--has-value': !!selectedItem
            },
            className
          )}
          disabled={disabled}
          onClick={handleToggle}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="prometheus-dropdown__value">
            {selectedItem ? selectedItem.label : placeholder}
          </span>
          
          <CaretIcon isOpen={isOpen} />
        </button>
        
        {isOpen && (
          <div className="prometheus-dropdown__menu">
            {children ? (
              children
            ) : (
              <ul className="prometheus-dropdown__list" role="listbox">
                {items.map((item) => (
                  <li key={item.value}>
                    <button
                      type="button"
                      className={clsx(
                        'prometheus-dropdown__item',
                        {
                          'prometheus-dropdown__item--selected': item.value === value,
                          'prometheus-dropdown__item--disabled': item.disabled,
                        }
                      )}
                      disabled={item.disabled}
                      onClick={() => handleItemSelect(item)}
                      role="option"
                      aria-selected={item.value === value}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      
      {helperText && (
        <span
          className={clsx(
            'prometheus-dropdown__helper-text',
            {
              'prometheus-dropdown__helper-text--error': error,
              'prometheus-dropdown__helper-text--success': success,
            }
          )}
        >
          {helperText}
        </span>
      )}
    </div>
  );
};

Dropdown.displayName = 'Dropdown';

export default Dropdown;